import React, { useMemo, useState } from 'react';
import { Button, Text, useToast, HStack } from '@chakra-ui/react';
import { FaDownload, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { primaryButtonProps, ghostButtonProps } from '../../../theme/adminUi';
import ReportPeriodBar from './ReportPeriodBar';
import ReportPreviewPanel from './ReportPreviewPanel';
import { defaultMonthRange, validatePeriod } from './reportUtils';

const CEM_COLUMNS = [
  'Numero',
  'Pais',
  'Clave_del_pais',
  'Idioma',
  'Id_del_concesionario',
  'Nombre_del_concesionario',
  'Chasis',
  'Empresa',
  'Tipo_de_Estudio',
  'Nombre_del_contacto',
  'Clave_de_la_ciudad_Movil',
  'Movil',
  'Correo_electronico',
  'Clave_de_la_ciudad',
  'Telefono_principal',
  'Ext_Tel_principal',
  'Clave_Ciudad_Tel_2',
  'Telefono_2',
  'Ext_Tel_2',
  'Clave_Ciudad_Tel_3',
  'Telefono_3',
  'Ext_Tel_3',
  'Modelo',
  'Ano_del_modelo',
  'Domicilio_del_cliente',
  'Colonia',
  'Ciudad',
  'Codigo_Postal',
  'Fecha_del_servicio',
  'Tipo_de_servicio',
  'Nombre_del_asesor',
  'Nombre_del_tecnico',
];

const PREVIEW_COLUMNS = [
  { key: 'Chasis', label: 'Chasis' },
  { key: 'Nombre_del_contacto', label: 'Contacto' },
  { key: 'Movil', label: 'Móvil' },
  { key: 'Marca', label: 'Marca' },
  { key: 'Modelo', label: 'Modelo' },
  { key: 'Ciudad', label: 'Ciudad' },
  { key: 'Fecha_del_servicio', label: 'Fecha servicio' },
  { key: 'Tipo_de_servicio', label: 'Tipo servicio' },
  { key: 'Nombre_del_tecnico', label: 'Técnico' },
];

export default function CemReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const defaults = useMemo(() => defaultMonthRange(), []);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  const load = async () => {
    const error = validatePeriod(startDate, endDate);
    if (error) {
      toast({ title: 'Período inválido', description: error, status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setLoading(true);
    try {
      const res = await getData(
        `/reports/cem?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      );
      setRows(res?.payload?.items || []);
      setHasPreviewed(true);
    } catch (err) {
      toast({
        title: 'Error al cargar el reporte CEM',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setRows([]);
      setHasPreviewed(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    const error = validatePeriod(startDate, endDate);
    if (error) {
      toast({ title: 'Período inválido', description: error, status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setDownloading(true);
    try {
      let data = rows;
      if (!hasPreviewed || !data.length) {
        const res = await getData(
          `/reports/cem?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        );
        data = res?.payload?.items || [];
        setRows(data);
        setHasPreviewed(true);
      }

      if (!data.length) {
        toast({
          title: 'Sin datos',
          description: 'No hay vehículos en el período seleccionado.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const sheetRows = [
        CEM_COLUMNS,
        ...data.map((row) => CEM_COLUMNS.map((col) => row[col] ?? null)),
      ];
      const ws = XLSX.utils.aoa_to_sheet(sheetRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'GridView_Data');
      XLSX.writeFile(wb, `PlanillaCEM_${startDate}_${endDate}.xlsx`);
    } catch (err) {
      toast({
        title: 'Error al descargar Excel',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminPageShell
      title="Reporte CEM"
      actions={
        <HStack spacing={2}>
          <Button
            {...ghostButtonProps}
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/admin/reports')}
          >
            Volver
          </Button>
          <Button
            {...primaryButtonProps}
            leftIcon={<FaDownload />}
            onClick={downloadExcel}
            isLoading={downloading}
          >
            Descargar Excel
          </Button>
        </HStack>
      }
    >
      <ReportPeriodBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={load}
        loading={loading}
        description="Vehículos únicos atendidos en el período. Si el mismo vehículo ingresó más de una vez, figura una sola vez con la visita más reciente."
      />

      <ReportPreviewPanel
        title="Vista previa"
        count={rows.length}
        countLabel={`${rows.length} vehículos`}
        loading={loading}
        emptyMessage={
          hasPreviewed
            ? 'No hay vehículos en este período.'
            : 'Seleccioná un período y generá la vista previa.'
        }
        emptyHint="Podés usar los atajos rápidos o elegir fechas personalizadas."
        columns={PREVIEW_COLUMNS}
        rows={rows}
        getRowKey={(row) => `${row.Chasis}-${row.Numero}`}
        renderCell={(row, col) => {
          const value = row[col.key];
          if (!value) return <Text color="gray.400">—</Text>;
          if (col.key === 'Chasis') {
            return <Text fontWeight="semibold" fontFamily="mono" fontSize="sm">{value}</Text>;
          }
          if (col.key === 'Tipo_de_servicio') {
            return <Text fontSize="sm" noOfLines={2}>{value}</Text>;
          }
          return <Text fontSize="sm">{value}</Text>;
        }}
      />
    </AdminPageShell>
  );
}
